// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, inject } from '@angular/core';
import { shuffle } from 'lodash-es';
import { interval, map, startWith } from 'rxjs';
import { UserService } from '../../services/user.service';

// seconds
const animationInterval = 3;

const greetings = {
  en: 'nice to see you',
  de: 'schön dich zu sehen',
  ja: 'お会いできて嬉しいです',
  pt_BR: 'que bom ver você',
  fr: 'ravi de te voir',
  es: 'me alegro de verte',
  hi: 'आपको देख कर अच्छा लगा',
  bn: 'আপনাকে দেখে ভালো লাগলো',
  te: 'మిమ్మల్ని చూడడం ఆనందంగా ఉంది',
  ta: 'உங்களை பார்த்ததில் மகிழ்ச்சி',
  mr: 'तुम्हाला पाहून आनंद झाला',
  ur: 'آپ کو دیکھ کر اچھا لگا',
  he: 'נחמד לראות אותך',
  zh_CN: '很高兴见到你',
  ar: 'من الجميل أن أراك',
  id: 'senang bertemu Anda',
  zh_TW: '很高興見到你',
  it: 'Piacere di vederti',
  ko: '만나서 반가워',
  eo: 'Estas plezuro vidi vin',
};

@Component({
  selector: 'app-hello-there',
  templateUrl: `./hello-there.component.html`,
  styleUrls: ['./hello-there.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [AsyncPipe],
})
export class HelloThere {
  protected userService = inject(UserService);

  greetingMessage = HelloThere.generateGreetings();

  classes$ = interval(1000 * animationInterval).pipe(
    map((i) => this.generateAnimation(i)),
    startWith(['animate-in'])
  );

  generateAnimation(currentIndex: number): string[] {
    const messageLength = this.greetingMessage.length;
    return this.greetingMessage.map((_, messageIndex) => {
      if ((currentIndex + 1) % messageLength === messageIndex) {
        return 'animate-in';
      }
      if (currentIndex % messageLength === messageIndex) {
        return 'animate-out';
      }
      return '';
    });
  }

  static generateGreetings(): string[] {
    // try to guess preferred language based on navigator.language
    // if we can't find it, default to english (0)
    const mainIndex =
      Object.entries(greetings).findIndex(([key]) =>
        navigator.language.match(new RegExp(`^${key}`, 'i'))
      ) ?? 0;
    const allGreetings = [...Object.values(greetings)];
    const first = allGreetings.splice(mainIndex, 1);
    return [...first, ...shuffle(allGreetings)];
  }
}
